/**
 * Infer the *kind* of work a just-sent message will trigger, so the chat can show a
 * non-intrusive activity bar with a meaningful label ("Researching the web…", "Generating
 * an image…") instead of a generic spinner. This is a lightweight client mirror of the
 * backend routers (artifacts.detectArtifactRequest, intent/mediaRouter, intent/tierRouter)
 * — purely cosmetic; the backend remains the source of truth for what actually runs.
 */
export type ActivityKind = 'artifact' | 'image' | 'music' | 'video' | 'research' | 'thinking';

export interface Activity {
  kind: ActivityKind;
  label: string;
}

// Mirror of artifacts.detectArtifactRequest's "wants an artifact" cue.
const ARTIFACT_RE = /\b(deck|slides?|presentation|powerpoint|pptx|pitch deck|flowchart|flow chart|diagram|process map|mind ?map|summari[sz]e|tl;?dr|recap|one[- ]?pager)\b/;
// Mirror of intent/mediaRouter generation + image-noun cues.
const IMAGE_RE = /\b(generate|create|draw|make|design|render|illustrat|paint|image of|picture of|photo of|a logo|poster|artwork|visuali[sz]e)\b.*\b(image|picture|photo|illustration|graphic|artwork|poster|logo|drawing|render)\b|\b(image|picture|photo|illustration|logo|poster) of\b/;
// Mirror of intent/mediaRouter video + music cues — so a song/clip request shows a real label.
const VIDEO_RE = /\b(video|clip|animation|animate|reel|footage)\b/;
const MUSIC_RE = /\b(song|music|track|jingle|beat|melody|tune|instrumental|lo-?fi|compose|composer|soundtrack|anthem|ballad)\b/;
// Mirror of intent/tierRouter RESEARCH_CUE.
const RESEARCH_RE = /\b(research|deep[ -]?dive|investigat|thesis|forecast|back[ -]?test|scenario|in[ -]depth|comprehensive|latest|news|today)\b/;

/**
 * Classify the message into the activity the user is most likely waiting on. Order
 * matters: an explicit artifact/media ask wins over a research cue; everything else is
 * "thinking" (a normal reply). `hasImage` nudges toward image work.
 */
export function inferActivity(text: string, hasImage = false): Activity {
  const t = (text || '').toLowerCase();
  if (ARTIFACT_RE.test(t)) return { kind: 'artifact', label: 'Putting your document together…' };
  if (hasImage) return { kind: 'image', label: 'Working on your image…' };
  if (VIDEO_RE.test(t)) return { kind: 'video', label: 'Rolling your video…' };
  if (MUSIC_RE.test(t)) return { kind: 'music', label: 'Composing your track…' };
  if (IMAGE_RE.test(t)) return { kind: 'image', label: 'Painting your image…' };
  if (RESEARCH_RE.test(t)) return { kind: 'research', label: 'Researching the web…' };
  return { kind: 'thinking', label: 'Thinking it through…' };
}
